/**
 * Material Cache Hook for Moon
 * Pre-creates all materials for all LOD levels to enable instant switching
 */

import { useEffect, useState, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import { createMoonMaterial } from '../materials/moonMaterial'
import { TILE_GRID } from '../textures/tileConfig'
import materialConfig from '../config/materialConfig.json'

export function useMaterialCache(lodLevels, preloadComplete) {
  const { gl } = useThree()
  const isWebGPU = gl.isWebGPURenderer || false
  const [materialCache, setMaterialCache] = useState({})
  const [isReady, setIsReady] = useState(false)
  const materialCacheRef = useRef({})

  useEffect(() => {
    if (!preloadComplete) return

    let isMounted = true
    const newCache = {}

    async function createAllMaterials() {
      
      for (const lod of lodLevels) {
        const quality = lod.name
        const gridConfig = TILE_GRID[quality]

        try {
          // Create Moon material
          const moonMaterial = await createMoonMaterial({
            tileTextures: [], // Empty - will use cache
            columns: gridConfig.columns,
            rows: gridConfig.rows,
            isWebGPU,
            quality,
            materialConfig,
          })

          newCache[quality] = {
            moon: moonMaterial,
          }
          
        } catch (error) {
          // Silent error handling
        }
      }

      if (isMounted) {
        setMaterialCache(newCache)
        materialCacheRef.current = newCache
        setIsReady(true)
      } else {
        // Cleanup if unmounted - dispose materials only, NOT cached textures
        // Textures are managed by textureCache.js and disposing them here would
        // cause use-after-free bugs when other components access them
        Object.entries(newCache).forEach(([quality, { moon }]) => {
          if (moon) {
            // Clear userData reference but don't dispose (texture is in shared cache)
            moon.userData.textureArray = null
            moon.dispose()
          }
        })
      }
    }

    createAllMaterials()

    return () => {
      isMounted = false
      // Dispose materials only - DO NOT dispose texture arrays as they're managed by textureCache
      // Disposing cached textures here would cause use-after-free bugs when other components 
      // access them from the shared cache
      Object.entries(materialCacheRef.current).forEach(([quality, { moon }]) => {
        if (moon) {
          // Clear userData reference but don't dispose (texture is in shared cache)
          moon.userData.textureArray = null
          moon.dispose()
        }
      })
      materialCacheRef.current = {}
    }
  }, [lodLevels, isWebGPU, preloadComplete])

  return { materialCache, isReady }
}

export default useMaterialCache